import { debounce, isEqual, omit } from 'lodash';
import { dispatchAction } from '@ardoq/rxbeach';
import { getExportsForHtmlView } from '@ardoq/export';
import { getExportLabels } from 'views/getExportLabels';
import { StreamedProps, getViewModel$ } from './viewModel$';
import { ViewContainer } from './atoms';
import type { Point } from '@ardoq/math';
import { ViewStreamShape } from './types';
import { zoomCenter } from 'atomicComponents/Zoomable/actions';
import { defaultLegendOnClick } from 'viewSettings/getRightMenuConfig';
import { MouseEvent, RefObject, createRef, Component } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { selectComponent } from 'streams/components/ComponentActions';
import { getComponentIdFromTarget, isClick } from './utils';
import { ZoomableViewLegendContainer } from 'tabview/relationshipDiagrams/atoms';
import { getAdditionalRowsForGraphViewLegend } from 'tabview/graphViews/graphViewLegend/graphViewLegendUtils';
import { getActiveDiffMode } from 'scope/activeDiffMode$';
import { isInScopeDiffMode } from 'scope/scopeDiff';
import { HeightOffset } from 'views/viewLegend/useViewLegendSubscription';
import { Subscription } from 'rxjs';
import { getSharedExportFunctions } from 'tabview/getSharedExportFunctions';
import { CapabilityMapSettingsBar } from './CapabilityMapSettingsBar';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import {
  focusedComponentChanged,
  hoveredComponentChanged,
} from 'tabview/actions';
import { CapabilityMap } from './CapabilityMap';
import {
  getActiveConditionalFormattingForLegend,
  ViewLegend,
} from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { connectInstanceWithProps } from 'streams/utils/connectInstanceWithProps';

const VIEW_ID = ViewIds.CAPABILITY_MAP;

type TempStruct = {
  height: number;
  node?: HTMLElement;
};

const getMaxLineCount = (container: HTMLElement, selector: string) => {
  const { node: highestNode } = Array.from<HTMLElement>(
    container.querySelectorAll(selector)
  )
    .map<TempStruct>(node => ({
      node,
      height: node.offsetHeight,
    }))
    .reduce(
      (highest, candidate) =>
        candidate.height > highest.height ? candidate : highest,
      { height: 0 }
    );
  return highestNode ? highestNode.getClientRects().length : 1;
};

type CapabilityMapProps = StreamedProps;

type CapabilityMapState = {
  maxLineCount: number;
  maxTitleLineCount: number;
};

const hasNoComponents = (viewModel: ViewStreamShape) =>
  !viewModel.componentTypes.length;
export class CapabilityMapView extends Component<
  CapabilityMapProps,
  CapabilityMapState
> {
  containerRef: RefObject<HTMLDivElement | null> = createRef();
  mouseDownPosition: Point = [0, 0];
  state = {
    maxLineCount: 1,
    maxTitleLineCount: 1,
  };
  isRequestCenter = false;
  lastViewModel: ViewStreamShape | null = null;
  toggleLegendSubscription: Subscription | null = null;

  checkAndSetRowCountDebounced = debounce(
    () => this.checkAndSetRowCount(),
    100
  );
  onNavigate = (event: MouseEvent) => {
    const componentId = getComponentIdFromTarget(event);
    if (!isClick(event, this.mouseDownPosition) || !componentId) {
      return;
    }
    dispatchAction(selectComponent({ cid: componentId }));
  };
  onMouseDown = (event: MouseEvent) => {
    const { clientX, clientY } = event;
    this.mouseDownPosition = [clientX, clientY];
  };
  onClick = (event: MouseEvent) => {
    event.stopPropagation();
    const componentId = getComponentIdFromTarget(event);
    if (
      !componentId ||
      componentId === this.props.viewModel.focusedComponentId
    ) {
      dispatchAction(hoveredComponentChanged(null), VIEW_ID);
      dispatchAction(focusedComponentChanged(null), VIEW_ID);
      return;
    }
    dispatchAction(focusedComponentChanged(componentId), VIEW_ID);
  };
  checkAndSetRowCount = (isRequestCenter = false) => {
    if (!this.containerRef.current) {
      return false;
    }
    this.isRequestCenter = isRequestCenter;
    const maxLineCount = getMaxLineCount(
      this.containerRef.current,
      '.leaf-node .leaf-name'
    );
    const maxTitleLineCount = getMaxLineCount(
      this.containerRef.current,
      '.container-title .container-name'
    );

    if (
      this.state.maxLineCount !== maxLineCount ||
      this.state.maxTitleLineCount !== maxTitleLineCount
    ) {
      this.setState({ maxLineCount, maxTitleLineCount });
      this.isRequestCenter = true;
      return true;
    }
    return false;
  };
  centerOrCheckHeights = () => {
    const willSetState = this.checkAndSetRowCount(this.isRequestCenter);
    if (this.isRequestCenter && !willSetState) {
      this.isRequestCenter = false;
      dispatchAction(zoomCenter({ viewId: VIEW_ID, duration: 0 }));
    }
  };

  render() {
    const { viewModel, viewState, i18nLocale } = this.props;
    if (!viewModel?.rootLayoutBox) {
      return null;
    }

    this.isRequestCenter =
      this.isRequestCenter ||
      !isEqual(
        omit(this.lastViewModel, ['focusedComponentId', 'hoveredComponentId']),
        omit(this.props.viewModel, ['focusedComponentId', 'hoveredComponentId'])
      );
    this.lastViewModel = this.props.viewModel;

    const {
      rootLayoutBox,
      legendComponentTypes,
      focusedComponentId,
      hoveredComponentId,
    } = viewModel;

    const activeDiffMode = getActiveDiffMode();
    const isScopeDiffMode = isInScopeDiffMode();

    const isEmptyView = hasNoComponents(viewModel);

    const activeConditionalFormattingForLegend =
      getActiveConditionalFormattingForLegend(i18nLocale);

    const additionalRows = getAdditionalRowsForGraphViewLegend({
      componentTypes: legendComponentTypes,
      referenceTypes: [],
      hasCollapsedNodes: false,
      hasReferenceParents: false,
      hasNonComponentNodes: false,
      isUserDefinedGrouping: false,
      showComponentSwatches: true,
      activeConditionalFormattingForLegend: {
        components: activeConditionalFormattingForLegend.components,
        tags: activeConditionalFormattingForLegend.tags,
        references: [],
      },
      showComponentShapes: false,
      showReferencesAsLines: false,
      selectableItems: false,
      isNewConditionalFormatting: false,
      i18nLocale,
    });

    return (
      <div
        className="tab-pane active"
        data-context-menu={OBJECT_CONTEXT_MENU_NAME}
      >
        <CapabilityMapSettingsBar
          viewState={viewState}
          viewModel={viewModel}
          i18nLocale={i18nLocale}
          exports={{
            ...getExportsForHtmlView({
              container: () => this.containerRef.current,
              exportedViewMetadata: {
                name: VIEW_ID,
                isViewpointMode: false,
              },
              ...getSharedExportFunctions(),
              labels: getExportLabels(i18nLocale),
            }),
            isDisabled: isEmptyView,
          }}
          legendOnClick={() => {
            defaultLegendOnClick({
              isLegendActive: !viewState.isLegendActive,
              viewId: VIEW_ID,
            });
          }}
        />

        <ViewContainer ref={this.containerRef} onClick={this.onClick}>
          {isEmptyView ? (
            <EmptyStateBase />
          ) : (
            <>
              <CapabilityMap
                rootLayoutBox={rootLayoutBox}
                onClick={this.onClick}
                onDoubleClick={this.onNavigate}
                onMouseDown={this.onMouseDown}
                hoveredComponentId={hoveredComponentId}
                focusedComponentId={focusedComponentId}
                i18nLocale={i18nLocale}
              />
              <HeightOffset>
                {heightOffset => (
                  <ZoomableViewLegendContainer
                    heightOffset={heightOffset || 0}
                    visible={viewState.isLegendActive}
                  >
                    <ViewLegend
                      componentTypes={[]}
                      referenceTypes={[]}
                      additionalRows={additionalRows}
                      activeDiffMode={isScopeDiffMode ? activeDiffMode : null}
                      labels={getViewLegendLabels(i18nLocale)}
                    />
                  </ZoomableViewLegendContainer>
                )}
              </HeightOffset>
            </>
          )}
        </ViewContainer>
      </div>
    );
  }

  componentDidMount() {
    setTimeout(this.centerOrCheckHeights);
    window.addEventListener('resize', this.checkAndSetRowCountDebounced);
  }

  componentDidUpdate() {
    setTimeout(this.centerOrCheckHeights);
  }

  componentWillUnmount() {
    this.checkAndSetRowCountDebounced.cancel();
    window.removeEventListener('resize', this.checkAndSetRowCountDebounced);
    this.toggleLegendSubscription?.unsubscribe();
  }
}

const ConnectedCapabilityMap = connectInstanceWithProps(
  CapabilityMapView,
  getViewModel$
);

export { ConnectedCapabilityMap };
